/**
 * What happens to the report now that it is designed.
 *
 * Three answers, one choice: keep it private, share it with a colleague, or send it on a
 * schedule. Each is a card; the one chosen shows what it will do and the single button that does
 * it. Nothing here edits the design — sharing and schedules attach to the saved report — so when
 * the design has unsaved changes the dialog says so first and offers to save them, because a
 * schedule runs the saved version and not the draft.
 */
import { useState } from 'react'
import clsx from 'clsx'
import { CalendarClock, Check, Loader2, Lock, Save, Users } from 'lucide-react'
import { apiErrorMessage, goldBtn, parseEmails } from '../studioUi'
import type { SavedView, ViewAclUpdate } from '../types'
import { StudioDialog } from './controls'
import { denseField, denseLabel } from './controlStyles'

type Choice = 'private' | 'share' | 'schedule'

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export function FinishDialog({
  view,
  dirty,
  busy,
  note,
  onNoteChange,
  onSaveVersion,
  onUpdateAcl,
  scheduleSummary,
  onOpenSchedule,
  onClose,
}: {
  view: SavedView
  /** The design has edits that are not in a saved version. */
  dirty: boolean
  busy: boolean
  note: string
  onNoteChange: (next: string) => void
  /** Saves the draft as a new version. Rejects when the server refuses, having said why. */
  onSaveVersion: () => Promise<void>
  onUpdateAcl: (acl: ViewAclUpdate) => Promise<void>
  /** One line on the schedules this report already has. */
  scheduleSummary: string
  onOpenSchedule: () => void
  onClose: () => void
}) {
  const sharedNow = Boolean(view.isShared) || (view.viewers ?? []).length > 0 || (view.editors ?? []).length > 0
  const [choice, setChoice] = useState<Choice>(sharedNow ? 'share' : 'private')
  const [emails, setEmails] = useState((view.viewers ?? []).join(', '))
  const [canEdit, setCanEdit] = useState(false)
  const [workspaceWide, setWorkspaceWide] = useState(Boolean(view.isShared))
  const [working, setWorking] = useState(false)
  const [result, setResult] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null)

  const run = async (write: () => Promise<void>, done: string) => {
    setWorking(true)
    setResult(null)
    try {
      await write()
      setResult({ tone: 'ok', text: done })
    } catch (err) {
      setResult({ tone: 'error', text: apiErrorMessage(err, 'That could not be saved.') })
    } finally {
      setWorking(false)
    }
  }

  const keepPrivate = () =>
    run(
      () => onUpdateAcl({ isShared: false, editors: [], viewers: [] }),
      'Private. Only you can open this report.',
    )

  const share = () => {
    const named = parseEmails(emails)
    const invalid = named.filter((address) => !EMAIL.test(address))
    if (invalid.length > 0) {
      setResult({ tone: 'error', text: `Not an email address: ${invalid.join(', ')}` })
      return Promise.resolve()
    }
    if (named.length === 0 && !workspaceWide) {
      setResult({ tone: 'error', text: 'Name at least one colleague, or share with the whole workspace.' })
      return Promise.resolve()
    }
    const editors = canEdit ? [...new Set([...(view.editors ?? []), ...named])] : (view.editors ?? [])
    return run(
      () => onUpdateAcl({ isShared: workspaceWide, editors, viewers: named }),
      workspaceWide && named.length === 0
        ? 'Shared with the whole workspace.'
        : `Shared with ${named.join(', ')}${canEdit ? ' — they can edit it' : ''}.`,
    )
  }

  const disabled = busy || working

  const cards: Array<{ id: Choice; title: string; hint: string; icon: typeof Lock }> = [
    { id: 'private', title: 'Keep it private', hint: 'Only you can open it', icon: Lock },
    { id: 'share', title: 'Share with a colleague', hint: 'Someone else can open, or edit, it', icon: Users },
    { id: 'schedule', title: 'Send it regularly', hint: 'Generate and email it on a schedule', icon: CalendarClock },
  ]

  return (
    <StudioDialog label="Finish this report" onClose={onClose} width="max-w-2xl">
      <div className="min-h-0 flex-1 overflow-y-auto p-4">
        <p className="text-[12px] text-stone-300">
          <span className="font-medium text-stone-100">{view.name}</span> is designed. Where does it go?
        </p>

        {dirty ? (
          <div
            data-testid="finish-unsaved"
            className="mt-3 rounded border border-amber-300/30 bg-amber-300/[0.06] p-3 text-[11px] text-amber-100"
          >
            <p>
              The design has changes that are not saved. Sharing and schedules attach to the saved report
              (v{view.version}), so save these first.
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <input
                value={note}
                onChange={(event) => onNoteChange(event.target.value)}
                placeholder="What changed (required to save a version)"
                aria-label="Change rationale for this version"
                className={`${denseField} min-w-[16rem] flex-1`}
              />
              <button
                type="button"
                disabled={disabled || !note.trim()}
                onClick={() => void run(onSaveVersion, 'Saved as a new version.')}
                className={goldBtn}
              >
                {working ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                Save version
              </button>
            </div>
          </div>
        ) : null}

        <div role="radiogroup" aria-label="What happens to this report" className="mt-3 grid gap-2 sm:grid-cols-3">
          {cards.map((card) => {
            const Icon = card.icon
            const selected = choice === card.id
            return (
              <button
                key={card.id}
                type="button"
                role="radio"
                aria-checked={selected}
                onClick={() => {
                  setChoice(card.id)
                  setResult(null)
                }}
                className={clsx(
                  'rounded-lg border p-3 text-left transition',
                  selected
                    ? 'border-amber-300/60 bg-amber-300/[0.08]'
                    : 'border-white/[0.08] bg-black/20 hover:border-amber-300/30',
                )}
              >
                <Icon className={clsx('h-4 w-4', selected ? 'text-amber-200' : 'text-stone-500')} />
                <span className="mt-2 block text-[12px] font-medium text-stone-100">{card.title}</span>
                <span className="mt-0.5 block text-[10px] leading-snug text-stone-500">{card.hint}</span>
              </button>
            )
          })}
        </div>

        <div className="mt-3 rounded-lg border border-white/[0.06] bg-black/20 p-3" data-testid={`finish-${choice}`}>
          {choice === 'private' ? (
            <>
              <p className="text-[11px] leading-relaxed text-stone-300">
                A private report is listed only for you. Nobody else in the workspace can open it, and
                nobody named on it keeps access.
                {sharedNow ? (
                  <span className="mt-1 block text-amber-200/90">
                    It is shared today: keeping it private ends that sharing.
                  </span>
                ) : null}
              </p>
              <button type="button" disabled={disabled} onClick={() => void keepPrivate()} className={`${goldBtn} mt-3`}>
                <Lock className="h-3.5 w-3.5" /> Keep private
              </button>
            </>
          ) : null}

          {choice === 'share' ? (
            <>
              <label className="block">
                <span className={denseLabel}>Colleagues&rsquo; email addresses</span>
                <input
                  value={emails}
                  onChange={(event) => setEmails(event.target.value)}
                  placeholder="uw@firm.com, pm@firm.com"
                  className={`${denseField} mt-1`}
                  aria-label="Colleagues to share with"
                />
              </label>
              <label className="mt-2 flex cursor-pointer items-start gap-2 text-[11px] text-stone-300">
                <input
                  type="checkbox"
                  checked={canEdit}
                  onChange={(event) => setCanEdit(event.target.checked)}
                  className="mt-0.5 rounded border-stone-600 bg-black/40 text-amber-300"
                />
                <span>
                  They can change the design too
                  <span className="block text-[10px] text-stone-500">
                    Otherwise they can open and run it, not edit it.
                  </span>
                </span>
              </label>
              <label className="mt-2 flex cursor-pointer items-start gap-2 text-[11px] text-stone-300">
                <input
                  type="checkbox"
                  checked={workspaceWide}
                  onChange={(event) => setWorkspaceWide(event.target.checked)}
                  className="mt-0.5 rounded border-stone-600 bg-black/40 text-amber-300"
                />
                <span>
                  Everyone in the workspace can open it
                  <span className="block text-[10px] text-stone-500">Anyone who can see reports.</span>
                </span>
              </label>
              <button type="button" disabled={disabled} onClick={() => void share()} className={`${goldBtn} mt-3`}>
                <Users className="h-3.5 w-3.5" /> Share
              </button>
            </>
          ) : null}

          {choice === 'schedule' ? (
            <>
              <p className="text-[11px] leading-relaxed text-stone-300">
                A schedule generates this report on its own — daily, weekly or monthly — and can email it to
                whoever you name. It runs the saved version of the design.
              </p>
              <p className="mt-1.5 text-[11px] text-stone-500">{scheduleSummary}</p>
              <button
                type="button"
                disabled={disabled}
                onClick={onOpenSchedule}
                className={`${goldBtn} mt-3`}
              >
                <CalendarClock className="h-3.5 w-3.5" /> Set up a schedule…
              </button>
            </>
          ) : null}

          {result ? (
            <p
              role={result.tone === 'error' ? 'alert' : 'status'}
              className={clsx(
                'mt-3 flex items-start gap-1.5 text-[11px]',
                result.tone === 'error' ? 'text-red-300' : 'text-emerald-300',
              )}
            >
              {result.tone === 'ok' ? <Check className="mt-0.5 h-3 w-3 shrink-0" /> : null}
              {result.text}
            </p>
          ) : null}
        </div>
      </div>
      <div className="flex shrink-0 items-center justify-end gap-2 border-t border-amber-300/10 px-4 py-3">
        <button
          type="button"
          onClick={onClose}
          className="rounded border border-stone-600 px-3 py-1.5 text-[11px] text-stone-300 hover:bg-white/5"
        >
          Done
        </button>
      </div>
    </StudioDialog>
  )
}
